// src/components/CreateNewExerciseModal.tsx
import Button from '../button';
import { Box } from '@mui/material';
import { useState, useEffect, useMemo } from "react";
import Drawer from '@mui/material/Drawer';
import { useTranslation } from 'react-i18next';
import "./create-new-exercise.scss"; // reuse styles
import Dropdown from "../dropdown/index";
import FileUploadPanel from "../exercise-image-upload";
import { Close } from '../../assets/images/index';
import { validateExerciseName } from "../../utils/utils";
import { showErrorToast, showSuccessToast } from "../../components/Toaster/index";
import { SearchExerciseService } from "../../services/search-exercise/search-exercise.service";
import { useAdminUserStore } from '../../utils/adminUserStore';


const CreateNewExerciseModal = ({
    open,
    onClose,
    fetchCounts,
    fetchExercisesFromList
}: {
    open: boolean,
    onClose: () => void,
    fetchCounts: () => void,
    fetchExercisesFromList: () => void
}) => {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const searchExerciseService = new SearchExerciseService();
    const [levelOptions, setLevelOptions] = useState([{ value: '', label: t('all') }]);
    const [difficultyOptions, setDifficultyOptions] = useState([{ value: '', label: t('all') }]);
    const [editLevel, setEditLevel] = useState('');
    const [editTopic, setEditTopic] = useState('');
    const [editDifficulty, setEditDifficulty] = useState('');
    const [contentImage, setContentImage] = useState<string | null>(null);
    const [graphImages, setGraphImages] = useState<string[] | null>(null);
    const [exerciseName, setExerciseName] = useState('');
    const [exerciseForExams, setExerciseForExams] = useState(false);
    const [exerciseNameError, setExerciseNameError] = useState("");
    const [examImage, setExamImage] = useState<string | null>(null);
    const isFormValid = useMemo(() => {
      const hasBasicFields =
        Boolean(
          exerciseName.trim() &&
          validateExerciseName(exerciseName) &&
          editLevel &&
          editTopic &&
          editDifficulty &&
          contentImage
        );
      if (exerciseForExams) {
        return hasBasicFields && examImage;
      }
      return hasBasicFields;
    }, [exerciseName, editLevel, editTopic, editDifficulty, contentImage, exerciseForExams, examImage]);
    const [topicOptions, setTopicOptions] = useState([{ value: '', label: t('all') }]);
    const [isTopicsLoading, setIsTopicsLoading] = useState(false);
    const { newExercise, setExerciseData } = useAdminUserStore();

    useEffect(() => {
      if (open && newExercise) {
        setExerciseName(newExercise.exerciseFileName || "");
        setEditLevel(newExercise.level || "");
        setEditTopic(newExercise.topic || "");
        setEditDifficulty(newExercise.difficulty || "");
      }
    }, [open, newExercise]);

    useEffect(() => {
        fetchLevels();
        fetchTopics();
        fetchDifficulties();
    }, []);

    const resetForm = () => {
      setExerciseName("");
      setExerciseNameError("");
      setEditLevel("");
      setEditTopic("");
      setEditDifficulty("");
      setContentImage(null);
      setGraphImages(null);
    };
    
    const handleClose = () => {
        resetForm();
        onClose();
    };

    const fetchLevels = async () => {
        try {
            const levels = await searchExerciseService.getLevels();
            const formatted = levels.map((l: any) => ({ value: l.code.toString(), label: l.name }));
            setLevelOptions([{ value: '', label: t('all') }, ...formatted]);
        } catch (error) {
            console.error("Failed to fetch levels", error);
        }
    };

    const fetchTopics = async () => {
        setIsTopicsLoading(true);
        try {
            const topics = await searchExerciseService.getTopics();
            const formatted = topics.map((t: any) => ({ value: t.code.toString(), label: t.name }));
            setTopicOptions([{ value: '', label: t('all') }, ...formatted]);
        } catch (error) {
            console.error("Failed to fetch topics", error);
        } finally {
            setIsTopicsLoading(false);
        }
    };


    const fetchDifficulties = async () => {
        try {
            const difficulties = await searchExerciseService.getDifficulties();
            const formatted = difficulties.map((d: any) => ({ value: d.code.toString(), label: d.name }));
            setDifficultyOptions([{ value: '', label: t('all') }, ...formatted]);
        } catch (error) {
            console.error("Failed to fetch difficulties", error);
        }
    };

    const validateForm = (form: {
        exerciseName: string;
        level: string;
        topic: string;
        difficulty: string;
        exerciseImage: string | null;
        graphImages: string[] | null;
        isTestExercise: boolean;
        examImage: string | null;
      }) => {
        if (!form.exerciseName.trim()) {
          return t('exerciseNameRequired');
        }
        if (!validateExerciseName(form.exerciseName)) {
          return t('exerciseNameInvalid');
        }
        if (!form.level) {
          return t('levelRequired');
        }
        if (!form.topic) {
          return t('topicRequired');
        }
        if (!form.difficulty) {
          return t('difficultyRequired');
        }
        if (!form.exerciseImage) {
          return t('exerciseImageRequired');
        }
        if (form.isTestExercise && !form.examImage) {
          return t('answersScreenshotRequired');
        }
        return null;
      };

    const handleCreate = async () => {
        setIsLoading(true);

        setExerciseNameError("");

        const formValues = {
            exerciseName,
            level: editLevel,
            topic: editTopic,
            difficulty: editDifficulty,
            exerciseImage: contentImage as any,
            graphImages: (Array.isArray(graphImages) ? graphImages : []) as any,
            isTestExercise: exerciseForExams,
            examImage
          };
        
          const error = validateForm(formValues);
          if (error) {
            showErrorToast(error);
            setIsLoading(false);
            return;
          }

        try {
          const formData = new FormData();
          formData.append("name", exerciseName);
          formData.append("topic_id", editTopic);
          formData.append("level_id", editLevel);
          formData.append("difficulty_id", editDifficulty);
          formData.append("is_test_exercise", exerciseForExams ? "true" : "false");
      
          if (contentImage) {
            // Convert base64 -> File
            const res = await fetch(contentImage);
            const blob = await res.blob();
            formData.append("exercise_image", blob, "exercise.png");
          }
      
          if (graphImages && Array.isArray(graphImages)) {
            for (let i = 0; i < graphImages.length; i++) {
              const res = await fetch(graphImages[i]);
              const blob = await res.blob();
              formData.append("chart_images", blob, `chart_${i}.png`);
            }
          }

          if (exerciseForExams && examImage) {
            const res = await fetch(examImage);
            const blob = await res.blob();
            formData.append("answer_image", blob, "answers.png");
          }
      
          const response = await searchExerciseService.createExerciseByImage(formData);
            if (response.success == true) {
                showSuccessToast(`${t('exerciseCreatedSuccessfully')}`);
                setExerciseData({
                  exerciseFileName: exerciseName,
                  level: editLevel,
                  topic: editTopic,
                  difficulty: editDifficulty,
                });
                resetForm();
                onClose();
                fetchCounts();
                fetchExercisesFromList();
            } else {
                showErrorToast(response.message || `${t('errorCreatingExercise')}`);
            }
      
        } catch (error: any) {
          console.error("Error creating exercise:", error);
          if (error.response) {
            const { error_code, message } = error.response.data.detail;
        
            switch (error_code) {
              case "EXERCISE_EXISTS":
                showErrorToast(t('exerciseNameExists'));
                break;
              case "INVALID_NAME":
                showErrorToast(t('exerciseNameInvalid'));
                break;
              default:
                showErrorToast(message || t("unexpectedErrorOccurred"));
            }
          } else {
            showErrorToast(t("unexpectedErrorOccurred"));
          }
        } finally {
          setIsLoading(false);
        }
      };

    return (
        <Drawer anchor="right" open={open} onClose={handleClose}>
            <Box className="drawer_content">
                <div className='excercise_display_header_container p-0'>
                    <div className="header_content">
                        <h4>{t('exerciseName')}:</h4>
                        <input
                          type="text"
                          placeholder={t('exerciseNamePlaceholder')}
                          value={exerciseName}
                          onChange={(e) => {
                            const value = e.target.value.trimEnd();
                            setExerciseName(value);
                          }}
                        />
                      <div className='excercise_input_wrapper'>
                        <input
                          type="checkbox"
                          id="header_checkbox"
                          checked={exerciseForExams}
                          onChange={(e) => setExerciseForExams(e.target.checked)}
                        />
                        <label htmlFor="header_checkbox">{t("exerciseForExams")}</label>
                      </div>
                    </div>
                    <button onClick={handleClose} className='btn_close'>
                        <img src={Close} alt="Close" />
                    </button>
                        <div className="exercise_type_bg">
                            <h4>{t('levelAndTypeExercise')}</h4>
                            <div className='excercise_display_header'>
                            <Dropdown
                                label={t('level')}
                                value={editLevel}
                                options={levelOptions}
                                onChange={(val) => setEditLevel(val.toString())}
                            />
                            <Dropdown
                                label={t('topic')}
                                value={editTopic}
                                options={topicOptions}
                                loading={isTopicsLoading}
                                onChange={(val) => setEditTopic(val.toString())}
                            />
                            <Dropdown
                                label={t('difficulty')}
                                value={editDifficulty}
                                options={difficultyOptions}
                                onChange={(val) => setEditDifficulty(val.toString())}
                            />
                                                </div>
                        </div>
                </div>
                <div className="excercise_input_sections">
                    <div className="excercise_input_wrapper">
                    <h5>{t('copyExecisePhoto')}</h5>
                        <FileUploadPanel
                            label={t('exerciseContent')}
                            type="content"
                            onFileChange={(img) => setContentImage(img as string)}
                            t={t}
                        />
                    </div>
                    <div className="excercise_input_wrapper excercise_input_vertical_wrapper">
                      <div>
                        <h5>{t('copyExerciseCharts')}</h5>
                        <FileUploadPanel
                          label={t('graphImages')}
                          type="graph-images"
                          onFileChange={(img) => setGraphImages(img as string[])}
                          t={t}
                        />
                      </div>

                      {exerciseForExams && (
                        <div>
                          <h5>{t('photocopyTestExercise')}</h5>
                          <FileUploadPanel
                            label={t('answerImage')}
                            type="content"
                            onFileChange={(img) => setExamImage(img as string)}
                            t={t}
                          />
                        </div>
                      )}
                  </div>
                </div>
                <div className='drawer_footer bg_footer'>
                    <Button text={t('cancel')} color='lightBlue' onClick={handleClose} />
                    <Button text={t('createExercise')} color='blue' loader={isLoading} onClick={handleCreate} disabled={!isFormValid}/>
                </div>
            </Box>
        </Drawer>
    );
};

export default CreateNewExerciseModal;
